// src/components/AssetDetailsTable.tsx
import { CalendarToday, Info } from '@mui/icons-material';
import {
  Box,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  tooltipClasses,
  type TooltipProps,
  Typography,
} from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import { StyledTableCell } from 'components/Table/StyledTableCell';
import React, { useMemo } from 'react';
import { type ComponentDetails } from 'types/squares.type';
import { GetIcons } from 'views/SurvivalAnalysis/Helper/Icons/GetIcons.function';
import componentsImage from 'views/SurvivalAnalysis/Helper/Icons/componenent.png';
import { ThemePalette } from 'views/SurvivalAnalysis/Helper/Icons/theme.squares';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface AssetDetailsTableProps {
  enableDarkTheme: boolean;
  details: ComponentDetails[];
  partsHealthComponent: any[];
  // filteredAssets: any[];
}

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400,
  },
});

// constants
const criticalityPriority = (criticality: string) => {
  switch (criticality) {
    case 'high':
      return 1; // Highest priority
    case 'med':
      return 2; // Medium priority
    case 'low':
      return 3; // Lowest priority
    default:
      return 4; // Default for undefined criticality
  }
};

// helpers
const getUniqueDetailsByEarliestFutureDate = (data: any[]) => {
  // Create a map to track the record with the earliest future squares_recommended_date
  const uniqueMap = new Map();

  data.forEach((item) => {
    const key = `${item.bumper_number}-${item.component}-${item.sub_component}`;
    if (
      !uniqueMap.has(key) ||
      new Date(uniqueMap.get(key).squares_recommended_date).getTime() >
        new Date(item.squares_recommended_date).getTime()
    ) {
      uniqueMap.set(key, item);
    }
  });

  // Convert the map back to an array
  return Array.from(uniqueMap.values());
};

const getBorderColor = (criticality: string): string => {
  switch (criticality?.toLowerCase()) {
    case 'low':
      return ThemePalette.typography.success;
    case 'med':
      return ThemePalette.typography.warning;
    default:
      return ThemePalette.typography.lightDanger;
  }
};

// commented as not used => do not remove #r1
// const filterBasedOnCategoryOfRiskOrCriticality = (data: ComponentDetails[]) => {
//   const filterHigh = data?.filter(
//     (item) =>
//       item.criticality?.toLowerCase() === 'high' ||
//       item.criticality?.toLowerCase() === 'eol' ||
//       item?.work_order_age_category?.toLowerCase() === 'high' ||
//       item?.work_order_age_category?.toLowerCase() === 'eol'
//   );
//   if (filterHigh.length > 0) {
//     return filterHigh;
//   }
//   const filterMedium = data?.filter(
//     (item) =>
//       item.criticality?.toLowerCase() === 'med' ||
//       item.work_order_age_category?.toLowerCase() === 'med'
//   );
//   if (filterMedium.length > 0) {
//     return filterMedium;
//   }
//   const filterLow = data?.filter(
//     (item) =>
//       item.criticality?.toLowerCase() === 'low' ||
//       item.work_order_age_category?.toLowerCase() === 'low'
//   );
//   if (filterLow.length > 0) {
//     return filterLow;
//   }
//   return data;
// };

const sortDataByCriticality = (data: ComponentDetails[]) => {
  try {
    if (!Array.isArray(data)) {
      throw new Error('Data not an array');
    }
    return [...data].sort((a, b) => {
      const priorityA = criticalityPriority(a?.work_order_age_category ?? '');
      const priorityB = criticalityPriority(b?.work_order_age_category ?? '');

      const criticalityComparison = priorityA - priorityB;
      if (criticalityComparison !== 0) {
        return criticalityComparison;
      }

      const dateA = new Date(a?.manufacture_recommended_date ?? '');
      const dateB = new Date(b?.manufacture_recommended_date ?? '');

      const timeA = isNaN(dateA.getTime()) ? Infinity : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? Infinity : dateB.getTime();

      return timeA - timeB;
    });
  } catch (error) {
    console.error('Error while sorting', error);
    return [];
  }
};

const AssetDetailsTable: React.FC<AssetDetailsTableProps> = ({
  enableDarkTheme,
  details,
  partsHealthComponent,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const filteredEnhancedDetails = useMemo(() => {
    const sortedData = sortDataByCriticality(details);

    // filterBasedOnCategoryOfRiskOrCriticality is commented as of now to show all the components
    // const tempFilteredData = filterBasedOnCategoryOfRiskOrCriticality(sortedData);
    // const temp = getUniqueDetailsByEarliestFutureDate(tempFilteredData);

    const temp = getUniqueDetailsByEarliestFutureDate(sortedData);
    return temp;
  }, [details]);

  return (
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table
        style={{
          background: palette.paperBackground,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              {t('component')}
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              {t('recommended_oem_service_frequency')}
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              {t('age_last_maintenance')}
            </TableCell>
            {/* <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Manufacturer Rec. Date
            </TableCell> */}
            <StyledTableCell>
              {t('recommended_maintenance_action')}
            </StyledTableCell>
            {/* <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              System Recommended Date
            </TableCell> */}
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Risk
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Criticality
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody
          style={{
            backgroundColor: palette.toolBarBackground,
          }}
        >
          {Array.isArray(filteredEnhancedDetails) &&
          filteredEnhancedDetails?.length > 0 ? (
            filteredEnhancedDetails?.map((detail, index) => {
              const subComponent = detail?.sub_component;
              const extractedSubComponent = detail?.extracted_sub_component;
              const componentName = detail?.component;
              const subComponentName = subComponent || extractedSubComponent;
              const isComponentAndSubComponentSame =
                subComponentName?.toLowerCase() ===
                componentName?.toLowerCase();
              let actionRecommended = 'Repair/Replace';

              const detailHealthData = partsHealthComponent.find(
                (item) =>
                  item.bumper_id === detail.bumper_number &&
                  item.component === detail.component &&
                  item.sub_component === detail.sub_component
              );
              if (detailHealthData) {
                actionRecommended =
                  detailHealthData?.recommended_action ?? 'Repair/Replace';
              }

              return (
                <TableRow
                  key={`${detail.bumper_number}-${index}`}
                  sx={{
                    borderLeft: `4px solid ${getBorderColor(
                      detail.criticality
                    )}`,
                  }}
                >
                  {/* Component Details */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <GetIcons
                        icon={componentsImage}
                        enableDarkTheme={enableDarkTheme}
                      />
                      <Box>
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body2"
                        >
                          {detail.component}
                        </Typography>
                        <Typography
                          style={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.lightGrey
                              : ThemePalette.typography.grey,
                          }}
                          variant="body2"
                        >
                          {isComponentAndSubComponentSame
                            ? ''
                            : subComponentName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Recommended Service Frequency */}
                  <TableCell>
                    <Box>
                      {detail.freq_calendar_days && (
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body2"
                        >
                          {detail?.freq_calendar_days && (
                            <CalendarToday sx={{ fontSize: '12px' }} />
                          )}
                          {detail?.freq_calendar_days &&
                            ` Every ${detail.freq_calendar_days}`}
                          {detail.freq_engine_hours &&
                            detail.freq_calendar_days && <span> or </span>}
                        </Typography>
                      )}

                      {detail.freq_engine_hours && (
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body2"
                        >
                          {detail.freq_engine_hours !== 'No Recommendation' && (
                            <SVG name="streetIcon" />
                          )}
                          {detail.freq_engine_hours !== 'No Recommendation' &&
                            ` Every ${detail.freq_engine_hours}`}
                        </Typography>
                      )}
                      {detail.maintenance_type && (
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body2"
                        >
                          {/* temp added will be removed #r1 */}
                          {actionRecommended}
                          {/* {   detail.maintenance_type} */}
                        </Typography>
                      )}
                      {((!detail.freq_calendar_days &&
                        !detail.freq_engine_hours) ||
                        detail.freq_engine_hours === 'No Recommendation') && (
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body2"
                        >
                          {t('on_need_basis')}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Age (Last Maintenance) */}
                  <TableCell>
                    {!isNaN(detail.age_in_days) && detail.age_in_days !== '' ? (
                      <>
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body2"
                        >
                          <SVG name="engineLight" />{' '}
                          {Number(detail.age_in_days) * 24} {t('hours')}
                        </Typography>
                        <Typography
                          style={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.lightGrey
                              : ThemePalette.typography.grey,
                          }}
                          variant="body2"
                        >
                          <CalendarToday sx={{ fontSize: '12px' }} />{' '}
                          {new Date(
                            new Date().setDate(
                              new Date().getDate() - Number(detail.age_in_days)
                            )
                          ).toLocaleDateString()}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        style={{
                          color: palette.textPrimary,
                        }}
                        variant="body2"
                      >
                        {t('wo_not_available')}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Manufacturer Rec. Date */}
                  {/* <TableCell>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography
                    style={{
                      color: palette.textPrimary,
                    }}
                    variant="body1"
                  >
                    {detail.manufacture_recommended_date}
                  </Typography>
                  <Typography
                    style={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.lightGrey
                        : ThemePalette.typography.grey,
                    }}
                    variant="subtitle2"
                    color="grey"
                  >
                    {(() => {
                      const daysDifference = Math.ceil(
                        (new Date(
                          detail.manufacture_recommended_date
                        ).getTime() -
                          new Date().getTime()) /
                          (1000 * 60 * 60 * 24)
                      );

                      if (daysDifference > 0) {
                        return `${daysDifference} Days from now`;
                      } else if (daysDifference < 0) {
                        return `${Math.abs(daysDifference)} Days overdue`;
                      } else {
                        return 'Immediate';
                      }
                    })()}
                  </Typography>
                </Box>
              </TableCell> */}

                  <TableCell>
                    <Typography
                      style={{
                        color: palette.textPrimary,
                      }}
                      variant="body2"
                    >
                      {actionRecommended}
                      {/* TODO: Update this once the data starts flowing in the api. */}
                      {/* {detail?.actions_performed
                    ? detail?.actions_performed
                    : detail?.maintenance_type} */}
                    </Typography>
                  </TableCell>

                  {/* System Recommended Date */}
                  {/* <TableCell>
                <Typography
                  style={{
                    color: palette.textPrimary,
                  }}
                  variant="body2"
                >
                  {detail.squares_recommended_date}
                </Typography>
                <Typography
                  style={{
                    color: enableDarkTheme
                      ? ThemePalette.typography.lightGrey
                      : ThemePalette.typography.grey,
                  }}
                  variant="subtitle2"
                  color="grey"
                >
                  {(() => {
                    const daysDifference = Math.ceil(
                      (new Date(detail.squares_recommended_date).getTime() -
                        new Date().getTime()) /
                        (1000 * 60 * 60 * 24)
                    );

                    if (daysDifference > 0) {
                      return `${daysDifference} Days from now`;
                    } else if (daysDifference < 0) {
                      return `${Math.abs(daysDifference)} Days overdue`;
                    } else {
                      return 'Immediate';
                    }
                  })()}
                </Typography>
              </TableCell> */}

                  {/* Risk */}
                  <TableCell>
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={2}
                      alignItems="center"
                    >
                      {(() => {
                        const category =
                          detail.work_order_age_category?.toLowerCase();
                        const color =
                          category === 'high' || category === 'eol'
                            ? getBorderColor(detail.work_order_age_category)
                            : category === 'med'
                            ? getBorderColor(detail.work_order_age_category)
                            : category === 'low'
                            ? getBorderColor(detail.work_order_age_category)
                            : palette.textPrimary;

                        return (
                          <>
                            <Typography
                              style={{ color }}
                              variant="body2"
                              textTransform="uppercase"
                            >
                              {detail?.work_order_age_category}
                            </Typography>

                            <CustomWidthTooltip
                              title={
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '4px',
                                  }}
                                >
                                  <span>
                                    {detail?.source === 'survival'
                                      ? 'Based on: Work Order Pattern'
                                      : detail?.source === 'sensor' ||
                                        detail?.source === 'anomaly'
                                      ? 'Based on: Sensor Data Analysis'
                                      : `Based on ${detail?.source} analysis`}
                                  </span>
                                  {detail?.derived_insight && (
                                    <span>
                                      {t('insight')}{' '}
                                      {detail.derived_insight
                                        .split('_')
                                        .map((word: string) =>
                                          word
                                            ? word.charAt(0).toUpperCase() +
                                              word.slice(1)
                                            : ''
                                        )
                                        .join(' ')}
                                    </span>
                                  )}
                                </div>
                              }
                              arrow
                            >
                              <Info style={{ color }} />
                            </CustomWidthTooltip>
                          </>
                        );
                      })()}
                    </Box>
                  </TableCell>

                  {/* Criticality */}
                  <TableCell>
                    <Typography
                      style={{
                        color:
                          detail.criticality === 'high' ||
                          detail.criticality === 'eol'
                            ? getBorderColor(detail?.criticality)
                            : detail.criticality === 'med'
                            ? getBorderColor(detail?.criticality)
                            : detail.criticality === 'low'
                            ? getBorderColor(detail?.criticality)
                            : palette.textPrimary,
                      }}
                      variant="body2"
                    >
                      {detail?.criticality?.toUpperCase()}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography textAlign={'center'} color={palette.textPrimary}>
                  {t('no_component_subcomponent_available_for_selected_asset')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AssetDetailsTable;
